/**
 * 🔴 SYNC-CLIENTES-CARTEIRA (19/10/2026) — a linha de contrato embaixo de cada cliente da Carteira de clientes.
 *
 * Pedido do Matheus: *"seria importante eu saber qual o contrato e tipo de escopo esse cliente tem conosco"*.
 *  · Um contrato: a etiqueta da situação (a MESMA da ficha do cliente: `STATUS_LABEL`/`STATUS_VARIANT`) + plano + sistemas
 *    cobertos — "Ativo · Manutenção Mensal · CFTV, Controle de acesso".
 *  · Mais de um: UMA linha, "N contratos · plano1, plano2"; ao clicar abre no lugar, uma linha por contrato (o `LinhaRecolhivel`
 *    canônico, o mesmo do aviso — ❌ nada de estilo novo).
 *  · Contrato sem sistema marcado: "sem sistema marcado" (caso real em 19/10: AMATI).
 *  · Cliente sem contrato Ativo, Em implantação ou Aviso prévio: nada (a linha não existe).
 */
import { Badge, LinhaRecolhivel } from '@/components/ui';
import { STATUS_LABEL, STATUS_VARIANT } from '@/lib/contratos-v2-store';
import type { ContratoDaCarteira } from '@/lib/crm/crm-store';

function UmContrato({ k }: { k: ContratoDaCarteira }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-ink-2 dark:text-ink-dark-2" data-testid="carteira-contrato">
      <Badge variant={STATUS_VARIANT[k.situacao]} size="sm">
        {STATUS_LABEL[k.situacao]}
      </Badge>
      <span>{k.planos.join(', ')}</span>
      <span aria-hidden>·</span>
      {k.sistemas.length ? (
        <span>{k.sistemas.join(', ')}</span>
      ) : (
        <span className="text-warning">sem sistema marcado</span>
      )}
    </span>
  );
}

export function LinhaDeContrato({ contratos }: { contratos: ContratoDaCarteira[] }) {
  if (contratos.length === 0) return null;
  if (contratos.length === 1) {
    return (
      <span className="mt-1 block">
        <UmContrato k={contratos[0]} />
      </span>
    );
  }
  const planos = [...new Set(contratos.flatMap((k) => k.planos))];
  /**
   * 🔴 19/10/2026 (AJUSTES-CARTEIRA, item C): a linha fechada escondia o contrato que não está Ativo — e "em implantação" ou
   * "em aviso prévio" é justamente o que o gestor quer ver de relance. ✅ Uma etiqueta por situação diferente de Ativo, com a
   * contagem, antes do resumo. Mesma etiqueta e mesmas cores da ficha (`STATUS_LABEL`/`STATUS_VARIANT`). Todos Ativos: nada.
   */
  const fora = (Object.keys(STATUS_LABEL) as Array<keyof typeof STATUS_LABEL>)
    .filter((s) => s !== 'ativo')
    .map((s) => ({ s, n: contratos.filter((k) => k.situacao === s).length }))
    .filter((x) => x.n > 0);
  return (
    <LinhaRecolhivel
      recolhe="sempre"
      className="mt-1"
      titulo={
        <span className="inline-flex flex-wrap items-center gap-1.5 text-xs text-ink-2 dark:text-ink-dark-2">
          <span>{contratos.length} contratos</span>
          {fora.length > 0 && <span aria-hidden>·</span>}
          {fora.map(({ s, n }) => (
            <Badge key={s} variant={STATUS_VARIANT[s]} size="sm" data-testid="carteira-situacao-fechada">
              {n} {STATUS_LABEL[s].toLowerCase()}
            </Badge>
          ))}
          <span aria-hidden>·</span>
          <span>{planos.join(', ')}</span>
        </span>
      }
      data-testid="carteira-varios-contratos"
    >
      <ul className="space-y-1">
        {contratos.map((k) => (
          <li key={k.id}>
            <UmContrato k={k} />
          </li>
        ))}
      </ul>
    </LinhaRecolhivel>
  );
}
